import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AiJobStatus, QueueCounts } from '@chefty/shared-types';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { Eyebrow, EmptyState, LiveBadge, Loading, Panel, StatusBadge, Tag } from '../components/ui';
import { Pagination } from '../components/Pagination';
import { fmtInt, fmtDateTime } from '../lib/format';

const STATUSES: AiJobStatus[] = [
  'pending',
  'processing',
  'needs_review',
  'completed',
  'failed',
  'cancelled',
];

// Which live counts to surface per queue, and the accent each reads as.
const TILES: { key: keyof Omit<QueueCounts, 'name'>; label: string; tone: string }[] = [
  { key: 'active', label: 'Active', tone: 'text-info' },
  { key: 'waiting', label: 'Waiting', tone: 'text-warn' },
  { key: 'delayed', label: 'Delayed', tone: 'text-cream-200' },
  { key: 'completed', label: 'Completed', tone: 'text-ok' },
  { key: 'failed', label: 'Failed', tone: 'text-bad' },
  { key: 'paused', label: 'Paused', tone: 'text-cream-400' },
];

function QueueCard({ q }: { q: QueueCounts }) {
  return (
    <Panel>
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mono text-sm text-cream-100">{q.name}</span>
        <LiveBadge label="live" />
      </div>
      <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-cream-600/10">
        {TILES.map((t) => (
          <div key={t.key} className="bg-ink-850/80 px-3 py-3 text-center">
            <div className={`tnum font-display text-2xl font-bold ${t.tone}`}>{fmtInt(q[t.key])}</div>
            <div className="mt-0.5 text-[0.62rem] uppercase tracking-wider text-cream-400">
              {t.label}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export const WorkerPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<AiJobStatus | undefined>(undefined);

  useEffect(() => setPage(1), [status]);

  // Live queue state — poll every 5s, silently.
  const queues = useAsync(() => adminApi.queues(), [], { intervalMs: 5000 });
  // Persisted job history — poll every 8s so the table tracks status changes.
  const jobs = useAsync(
    () => adminApi.jobs({ page, pageSize: 25, status }),
    [page, status],
    { intervalMs: 8000 },
  );

  return (
    <div className="space-y-6">
      <section>
        <div className="mb-3 flex items-center gap-3">
          <Eyebrow>Queues</Eyebrow>
          <LiveBadge label="auto-refresh 5s" />
        </div>
        {queues.loading && !queues.data ? (
          <Loading label="Reading queues…" />
        ) : queues.error ? (
          <EmptyState title="Couldn’t reach the queue" hint={queues.error} />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {queues.data?.queues.map((q) => <QueueCard key={q.name} q={q} />)}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Eyebrow>Job history</Eyebrow>
            <LiveBadge label="auto-refresh 8s" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <FilterChip active={status === undefined} onClick={() => setStatus(undefined)} label="All" />
            {STATUSES.map((s) => (
              <FilterChip
                key={s}
                active={status === s}
                onClick={() => setStatus(s)}
                label={s.replace('_', ' ')}
              />
            ))}
          </div>
        </div>

        <Panel pad={false} className="overflow-hidden">
          {jobs.loading && !jobs.data ? (
            <Loading label="Loading jobs…" />
          ) : jobs.error ? (
            <EmptyState title="Couldn’t load jobs" hint={jobs.error} />
          ) : jobs.data && jobs.data.items.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cream-600/15 text-left text-[0.68rem] uppercase tracking-[0.14em] text-cream-400">
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Source</th>
                      <th className="px-5 py-3 font-semibold">Owner</th>
                      <th className="px-5 py-3 font-semibold">Detail</th>
                      <th className="px-5 py-3 text-right font-semibold">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cream-600/10">
                    {jobs.data.items.map((j) => (
                      <tr key={j.id} className="transition-colors hover:bg-saffron-500/[0.04]">
                        <td className="px-5 py-3">
                          <StatusBadge status={j.status} />
                        </td>
                        <td className="px-5 py-3">
                          <Tag>{j.sourceType}</Tag>
                        </td>
                        <td className="px-5 py-3">
                          {j.userEmail ? (
                            <Link
                              to={`/users/${j.userId}`}
                              className="text-cream-300 hover:text-saffron-300"
                            >
                              {j.userEmail}
                            </Link>
                          ) : (
                            <span className="font-mono text-xs text-cream-400">
                              {j.userId.slice(0, 8)}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          {j.error ? (
                            <span className="block max-w-xs truncate text-xs text-bad/80" title={j.error}>
                              {j.error}
                            </span>
                          ) : (
                            <span className="text-xs text-cream-600">—</span>
                          )}
                        </td>
                        <td className="tnum px-5 py-3 text-right text-xs text-cream-400">
                          {fmtDateTime(j.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={jobs.data.page}
                totalPages={jobs.data.totalPages}
                total={jobs.data.total}
                onPage={setPage}
              />
            </>
          ) : (
            <EmptyState title="No jobs" hint={status ? `No ${status.replace('_', ' ')} jobs` : undefined} />
          )}
        </Panel>
      </section>
    </div>
  );
};

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
        active
          ? 'border-saffron-500/50 bg-saffron-500/10 text-saffron-300'
          : 'border-cream-600/15 text-cream-400 hover:border-cream-600/30 hover:text-cream-200'
      }`}
    >
      {label}
    </button>
  );
}
